import { useEffect, useState } from 'react';
import type { ViewId } from '../state';
import { api } from '../lib/api';

type Props = {
    onNavigate: (id: ViewId) => void;
    /** Returns a promise so the view can await it and refresh the bank
     *  after the buy lands. Parent already awaits the backend call. */
    onBuyTicket: () => Promise<void> | void;
    bank: number;
    ticketPrice: number;
};

/**
 * FAST GIVEAWAY: pay a ticket, 1% chance to win the pot.
 * The bank + ticket price + recent winners come from the server. The actual
 * ticket purchase is performed by the parent (App.tsx) so the result modal
 * can be rendered consistently with the other game flows.
 */
export function FastGiveawayView({ onNavigate, onBuyTicket, bank: initialBank, ticketPrice: initialPrice }: Props) {
    const [bank, setBank] = useState(initialBank);
    const [ticketPrice, setTicketPrice] = useState(initialPrice);
    const [winners, setWinners] = useState<Array<{ name: string; amount: string }>>([]);
    const [myHistory, setMyHistory] = useState<Array<{
        id: number; cost: number; won: boolean; payout: number; createdAt: string;
    }> | null>(null);
    const [busy, setBusy] = useState(false);

    /** Pull the latest bank + history. Used both on mount/interval AND right
     *  after a ticket buy so the displayed bank reflects the new state
     *  instead of sitting on the stale value the view captured in props. */
    const refresh = async () => {
        try {
            const r = await api.getFast();
            setBank(r.bank);
            setTicketPrice(r.ticketPrice);
        } catch { /* keep prior */ }
        try {
            const r = await api.fastFeed();
            setWinners(
                r.items
                    .filter((i) => i.won)
                    .slice(0, 5)
                    .map((i) => ({
                        name: i.username ? '@' + i.username : (i.firstName ?? 'Аноним'),
                        amount: `${i.payout.toLocaleString('ru-RU')} BSP`,
                    })),
            );
        } catch { /* keep prior */ }
        try {
            const r = await api.fastMyHistory();
            setMyHistory(r.items);
        } catch { /* keep prior */ }
    };

    // Initial load + lightweight polling so the bank stays fresh while the
    // user is on this view (someone else's ticket purchase grows the pot
    // and they see it move).
    useEffect(() => {
        void refresh();
        const id = window.setInterval(() => { void refresh(); }, 8_000);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If the parent's snapshot of the bank changes (it polls on the menu
    // too), pick it up immediately — useful right after the user wins or
    // buys a ticket and the parent already has fresh data.
    useEffect(() => { setBank(initialBank); }, [initialBank]);

    const handleBuy = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onBuyTicket();
        } finally {
            setBusy(false);
            // Force-refresh after a buy so the bank reflects the new state
            // without waiting for the polling interval to come around.
            void refresh();
        }
    };

    return (
        <div className="view">
            <h2 className="text-center fw-900 text-accent" style={{ fontSize: 18, letterSpacing: 1 }}>
                FAST GIVEAWAY
            </h2>

            <div className="info-card" style={{ borderColor: 'var(--accent)', textAlign: 'center' }}>
                <p className="popup-text">Каждый может попробовать вытащить билет.</p>
                <p className="popup-text">Если он неудачный, ставка уходит в общий банк.</p>
                <p className="popup-text">
                    Шанс вытащить <b>1%</b> у любого. В случае победы вы получаете <b>ВЕСЬ БАНК</b>.
                </p>
            </div>

            <div className="text-center" style={{ margin: '8px 0' }}>
                <div className="text-muted f-12 fw-900" style={{ marginBottom: 4 }}>ОБЩИЙ БАНК</div>
                <div className="giveaway-bank-amount">
                    💸 {bank.toLocaleString('ru-RU')} <small style={{ fontSize: 14 }}>BSP</small>
                </div>
            </div>

            <button type="button" className="action-btn btn-gold" onClick={handleBuy} disabled={busy}>
                {busy ? '…' : `ВЗЯТЬ БИЛЕТ (${ticketPrice} BSP)`}
            </button>

            <div className="info-card">
                <h3 className="text-center fw-900" style={{ color: '#fff', fontSize: 11, marginBottom: 4 }}>
                    ПОСЛЕДНИЕ ПОБЕДИТЕЛИ
                </h3>
                {winners.length === 0 && (
                    <p className="text-muted text-center f-12">Пока никто не выигрывал.</p>
                )}
                {winners.map((w, i) => (
                    <div key={i} className="top-row">
                        <span className="top-name">{w.name}</span>
                        <span className="top-val text-green">{w.amount}</span>
                    </div>
                ))}
            </div>

            {myHistory && myHistory.length > 0 && (
                <div className="info-card">
                    <h3 className="text-center fw-900" style={{ color: '#fff', fontSize: 11, marginBottom: 4 }}>
                        МОЯ ИСТОРИЯ
                    </h3>
                    {myHistory.slice(0, 10).map((t) => (
                        <div className="top-row" key={t.id}>
                            <span className="top-name">
                                {t.won ? '🎉 Выигрыш' : '😔 Мимо'} ·{' '}
                                {new Date(t.createdAt).toLocaleString('ru-RU', {
                                    day: '2-digit', month: '2-digit',
                                    hour: '2-digit', minute: '2-digit',
                                })}
                            </span>
                            <span
                                className="top-val"
                                style={{ color: t.won ? 'var(--green)' : 'var(--text-muted)' }}
                            >
                                {t.won ? `+${t.payout}` : `−${t.cost}`} BSP
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <button type="button" className="back-btn" onClick={() => onNavigate('games')}>
                НАЗАД В ИГРЫ
            </button>
        </div>
    );
}
